import * as SubscriptionService from './SubscriptionsService';
import * as StorefrontApiService from './StorefrontApiService';
import * as MiddlewareService from './MiddlewareService';
import dayjs from '@root/lib/packages/dayjs';
import { TIMEZONE } from '@root/lib/constants';
import MenuWeek from '@root/lib/classes/MenuWeek';

const DAYS = new Map([
  [0, 'SUN'],
  [1, 'MON'],
  [2, 'TUE'],
  [3, 'WED'],
  [4, 'THU'],
  [5, 'FRI'],
  [6, 'SAT'],
]);

/**
 * Rolls the day over to the next day if the time is after 2pm CST, sets the time 
 * to 9am CST, and adds the buffer days to the date
 * 
 * @param {number} buffer 
 * @param {dayjs.Dayjs} date 
 */
const adjustDate = (buffer, date) => {
  let d = dayjs(date.valueOf()).tz(TIMEZONE);

  // Adding a day to buffer if after 2 PM CST/CDT
  if (d.hour() >= 14) {
    d = d.add(1, 'day');
  }

  // Normalize time to be 9 to avoid menu roll over issues
  d = d.set('hour', 9)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0);
  return d.add(buffer, 'days');
}

/**
 * 
 * @param {number} buffer
 * @param {Date|dayjs.Dayjs} n
 */
const calculateRelativeDeliveryDates = (buffer, nowDate = new Date()) => {
  const adjustedDate = adjustDate(buffer, nowDate);
  const currMenuWeek = MenuWeek.fromDate(nowDate);
  const nextMenuWeek = currMenuWeek.next();

  // Adding days until we get the right day
  const relativeDates = [];
  for (let i = 0; i < 14; i++) {
    const date = nextMenuWeek.startDate.add(i, 'days');
    const menuWeek = MenuWeek.fromDate(date);
    if (menuWeek.menuId === nextMenuWeek.menuId) continue;
    if (date.isBefore(adjustedDate)) continue;

    relativeDates.push({
      isEarlyWeek: menuWeek.menuId === nextMenuWeek.menuId,
      menuId: menuWeek.menuId,
      day: date.tz().format('ddd').toUpperCase(),
      date,
    });
  }

  return relativeDates;
}

/**
 * Calculates the reletive delivery dates and retruns the first allowed date
 * 
 * @param {string[]} allowedDeliveryDays 
 * @param {number} buffer
 * @param {Date|dayjs.Dayjs}
 */
const getFirstAvailableDeliveryDay = (allowedDeliveryDays, buffer, nowDate = new Date()) => {
  const deliveryDates = calculateRelativeDeliveryDates(buffer, nowDate);

  for (const deliveryDate of deliveryDates) {
    let day = deliveryDate.day;
    if (day === 'MON') day = 'NEXT_MON';

    // Checking if the delivery day is in the valid delivery days array
    if (!allowedDeliveryDays.includes(day)) continue;

    return deliveryDate.date;
  }

  return null;
}

/**
 * Doesn't actually get the date range of the delivery cause the client
 * keeps changing what the date means so ignore the name, it's just legacy
 * 
 * @param {Date|dayjs.Dayjs} deliveryDate 
 */
const calculateDateRange = (deliveryDate) => {
  const week = MenuWeek.fromDate(deliveryDate);
  return week.monday;
}

/**
 * Calculates the menu id for the given date
 * 
 * @param {Date|dayjs.Dayjs} date 
 */
const calculateMenuId = (date) => {
  const menuWeek = MenuWeek.fromDate(date);
  return menuWeek.menuId;
}

/**
 * Gets the abbriviated day name for a date
 * 
 * @param {Date} date 
 */
const getDayName = (date) => {
  const newDate = new Date(date.getTime());
  newDate.setUTCHours(15);
  return DAYS.get(newDate.getUTCDay());
}

/**
 * Converts a grapql id into a numerical id
 * 
 * @param {string} decodedId
 */
const toLegacyId = (decodedId) => {
  const [ ,legacyId ] = decodedId.match(/^gid:\/\/shopify\/.+\/(\d+)$/);
  return parseInt(legacyId);
}

/**
 * Gets the meals and time slot data for the order date 
 * 
 * @param {number} boxId 
 * @param {Date} orderDate 
 * @param {boolean} local 
 */
const getMealsForOrderDate = async (boxId, orderDate, local = false) => {
  const timeSlot = await SubscriptionService.timeSlots.forOrderDate(boxId, orderDate, local);
  const products = await getMealsForTimeSlot(timeSlot);
  return [products, timeSlot];
}

/**
 * Gets the meals for the time slot provided
 * 
 * @param {{
 *   box_products: Array<{
 *     platform_variant_id: string;
 *   }>
 * }} timeSlot
 * @returns 
 */
const getMealsForTimeSlot = async (timeSlot) => {
  const variantIds = timeSlot.box_products.map(i => i.platform_variant_id);
  const variantsData = await StorefrontApiService.getVariantsById(variantIds, [
    'sku',
    'id',
    'title',
    'quantityAvailable',
    'options: selectedOptions', [
      'name',
      'value',
    ],
    'images: image', [
      'alt: altText',
      'mediumSrc: transformedSrc(maxWidth: 640)',
      'smallSrc: transformedSrc(maxWidth: 320)',
    ],
    'price: priceV2', [
      'amount',
    ],
    'product', [
      'id',
      'description',
      'tags',
      'type: productType',
      'title',
      'vendor',
      'handle',
    ],
    'metafields (identifiers: [{namespace: "nutrition",key: "calories",},{namespace: "nutrition",key: "protein",},{namespace: "nutrition",key: "carbs",},{namespace: "nutrition",key: "fat",},{namespace: "nutrition",key: "sugar",},{namespace: "nutrition",key: "fiber",},{namespace: "nutrition",key: "sat_fat",},{namespace: "nutrition",key: "sodium",},{namespace: "instructions",key: "heating",},{namespace: "ingredients",key: "details",}])', 
    [
          'namespace',
          'type',
          'key',
          'value',
        ],
  ]);

  // Cleaning up the variant data
  const products = new Map();
  for (let variantData of variantsData.values()) {
    if (variantData === null) continue;
    const variant = {
      ...variantData,
      id: toLegacyId(variantData.id),
      price: StorefrontApiService.decimalToCents(variantData.price.amount),
      metafields: variantData.metafields.reduce((metafields, metafield) => ({
        ...metafields,
        [`${metafield.namespace}.${metafield.key}`]: metafield.type !== 'JSON_STRING'
          ? metafield.value
          : JSON.parse(metafield.value),
      }), {}),
      product: {
        ...variantData.product,
        id: toLegacyId(variantData.product.id),
      },
    };

    // Adding the variant to a product if one exists and creating the product 
    // and then adding the variant if one does not already exist
    if (products.has(variant.product.id)) {
      const product = products.get(variant.product.id);
      variant.product = product;
      product.variants.push(variant);
      product.variants.sort((a, b) => a.id - b.id);
    } else {
      const product = variant.product;
      product.variants = [variant];
      products.set(product.id, product);
    }
  }

  return Array.from(products.values());
}

/**
 * Reapplies discounts onto the line items
 * 
 * @param {{
 *   total: number;
 *   totalDiscount: number;
 *   subtotal: number;
 *   lineItems: Array<{
 *     linePrice: number;
 *     discounts: Array<{
 *       allocatedAmount: number;
 *       discountApplication: {
 *         allocationMethod: 'EACH' | 'ACROSS';
 *         code: string;
 *         applicable: boolean;
 *         targetType: 'LINE_ITEM';
 *         type: 'DiscountCodeApplication';
 *         value: {
 *           amount?: number;
 *           percentage?: number;
 *           type: 'MoneyV2' | 'PricingPercentageValue';
 *         }
 *       }
 *     }>;
 *   }>;
 * }} cart
 */
const reapplyDiscountApplications = (cart) => {
  const discountApplications = new Map();
  const reappliedCart = {...cart};
  // Getting all unique discount applications and applying targets to them
  for (const lineItem of cart.lineItems) {
    if (lineItem.discounts.length === 0) continue;
    for (const discountAllocation of lineItem.discounts) {
      if (discountAllocation.discountApplication.code === undefined) continue; // Only accounting for discount code discounts
      const application = discountApplications.get(discountAllocation.discountApplication.code) ?? {
        ...discountAllocation.discountApplication,
        targetsTotal: 0,
        targets: [],
      };
      discountApplications.set(application.code, application);
      application.targets.push(lineItem);
      
      // Calculating the allocation left if method is accross
      if (application.allocationMethod === 'ACROSS') {
        application.targetsTotal += lineItem.linePrice;
        application.totalAllocation = application.totalAllocation ?? 0;
        application.allocationLeft = application.allocationLeft ?? 0;

        if ('percentage' in application.value) {
          const amount = Math.round(lineItem.linePrice * (application.value.percentage / 100));
          application.totalAllocation += amount; 
          application.allocationLeft += amount;
        } else {
          const amount = application.value.amount;
          application.totalAllocation = amount; 
          application.allocationLeft = amount;
        }
      }
    }
  }

  reappliedCart.lineItems = reappliedCart.lineItems.map(li => li.discounts.length === 0 ? li : {
    ...li,
    discounts: li.discounts.map(discountAllocation => {
      const discountApplication = discountApplications.get(discountAllocation.discountApplication.code);
      if (!discountApplication) return discountAllocation;
      const {
        targets,
        allocationMethod,
        value,
      } = discountApplication;
      const isLast = targets.slice(-1).pop() === li;
      
      // Applying discounts for across method
      if (allocationMethod === 'ACROSS') {
        const { totalAllocation, targetsTotal } = discountApplication;
        const allocationPercentage = li.linePrice / targetsTotal;
        const allocatedAmount = isLast
          ? discountApplication.allocationLeft
          : Math.round(allocationPercentage * totalAllocation);
        discountApplication.allocationLeft -= allocatedAmount;
        return {
          ...discountAllocation,
          allocatedAmount,
        }
      } 
      
      // Applying discounts for each method only if the value is percentage as fixed amount
      // doesn't need reapplying
      if ('percentage' in value) {
        const allocatedAmount = Math.round(li.linePrice * (value.percentage / 100));
        return {
          ...discountAllocation,
          allocatedAmount,
        };
      }

      return discountAllocation;
    })
  });

  // Calutating the total amount discounted on all line items
  reappliedCart.totalDiscount = reappliedCart.lineItems.reduce((a, li) => {
    return a + li.discounts.reduce((a, d) => a + d.allocatedAmount, 0);
  }, 0);

  reappliedCart.total = Math.max(0, reappliedCart.subtotal + reappliedCart.totalTax - reappliedCart.totalDiscount);
  return reappliedCart;
}

/**
 * Calculates the status of an order
 * 
 * @param {dayjs.Dayjs} deliveryDate 
 * @param {number} transitDays
 * @param {dayjs.Dayjs} [now]
 */
const calculateDeliveryStatus = (deliveryDate, transitDays, now = dayjs()) => {
  const shippingDate = deliveryDate.subtract(transitDays, 'days')
    .tz(TIMEZONE)
    .set('hours', 9)
    .set('minutes', 0)
    .set('seconds', 0)
    .set('milliseconds', 0);
  
  return now.isBefore(shippingDate)
    ? 'Scheduled'
    : now.isBefore(deliveryDate)
      ? 'Shipped'
      : 'Delivered';
}

/**
 * Determines if we are currently in the processing window (using the server's time).
 * Processing window is Wednesday 9am CST/CDT to Thursday 10am CST/CDT
 */
const isInProcessingWindow = async () => {
  const unix = await MiddlewareService.getTime();
  const time = dayjs.unix(unix).tz(TIMEZONE);

  // Start and end times of the processing window expressed as hours since the Sunday 00:00
  const start = 3 * 24 + 9; // Wednesday @ 9am (inclusive)
  const end = 4 * 24 + 10; // Thursday @ 10am (exclusive)
  const now = time.day() * 24 + time.hour();

  return now >= start && now < end;
}

export {
  calculateDeliveryStatus,
  reapplyDiscountApplications,
  getMealsForOrderDate,
  getMealsForTimeSlot,
  calculateRelativeDeliveryDates,
  calculateMenuId,
  getDayName,
  isInProcessingWindow,
  calculateDateRange,
  getFirstAvailableDeliveryDay,
}